import React, { useState, useEffect } from "react";
import {
    Typography,
    Button,
    Card,
    withStyles,
    createStyles,
    useTheme,
    Theme,
    useMediaQuery,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
} from "@material-ui/core";
import AppColors from './../colors/AppColors';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import { cyan, green, grey, red, yellow } from "@material-ui/core/colors";
import { NumberUtils } from '../utils/NumberUtils';
import Routes from "../constants/Routes";
import DigitsView from "../views/DigitsView";
import { faLightbulb } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Constants } from "../constants/Constants";
import EndPoints from "../net/EndPoints";
import TableEntry from "../models/TableEntry";
import UsersManager from "../managers/UsersManager";
import NextRollCountDownTimer from "../views/NextRollCountDownTimer";
import { GameType } from "../enums/GameType";
import axios from "axios";
import { useDispatch } from "react-redux";
import LiveStats from "./LiveStats";
import RequestUtils from "../utils/RequestUtils";
import GenUtils from "../utils/GenUtils";
import Game from "../models/Game";
import ExceptionUtils from "../utils/ExceptionUtils";

const StyledTableCell = withStyles((_theme: Theme) =>
    createStyles({
        head: {
            backgroundColor: AppColors.CARD_BACKGROUND_COLOR,
            color: grey[200],
            fontWeight: 600,
            borderBottomLeftRadius: 0,
            borderTopRightRadius: 0,
            borderBottomRightRadius: 0,
            borderTopLeftRadius: 0,
            paddingTop: 10,
            paddingBottom: 10,
            paddingLeft: 5,
            paddingRight: 5,
            border: `1px solid ${AppColors.CARD_BACKGROUND_COLOR}`
        },
        body: {
            fontSize: 15,
            paddingTop: 16,
            paddingBottom: 16,
            fontWeight: 600,
            color: grey[200],
            paddingRight: 10,
            borderColor: AppColors.CARD_BACKGROUND_COLOR,
            borderWidth: 1,
            border: `1px solid ${AppColors.CARD_BACKGROUND_COLOR}`
        },
    }),
)(TableCell);

const StyledTableRow = withStyles((_theme: Theme) =>
    createStyles({
        root: {
            backgroundColor: AppColors.CARD_BACKGROUND_COLOR,
            '&:nth-of-type(odd)': {
                backgroundColor: AppColors.CARD_BACKGROUND_COLOR_LIGHT,
            },
        },
    }),
)(TableRow);

const FreeBTC = React.forwardRef(function (_props: any, _ref: any) {

    const theme = useTheme();
    const inMobileView = useMediaQuery(theme.breakpoints.down(731));
    const belowSevenHundred = useMediaQuery(theme.breakpoints.down(700));
    const blackCardsCanGoFlat = useMediaQuery(theme.breakpoints.down(1100));
    const [docLoaded,] = useState(false);
    const [game, setGame] = useState<Game>();
    const [rolled, setRolled] = useState(true);
    const [rolling, setRolling] = useState(false);
    const [roll, setRoll] = useState(0);
    const [loading, setLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState("");
    const [wonAmount, setWonAmount] = useState(0);
    const [tableEntries, setTableEntries] = useState<TableEntry[]>([]);

    const [openDialog, setOpenDialog] = React.useState(false);

    const dispatch = useDispatch();

    const handleDialogClose = (multiply: boolean) => {
        setOpenDialog(false);
        if (multiply) {
            window.location.href = Routes.MULTIPLY_BTC;
        }
    }

    const freeRollCountDownTimerCallBack = () => {
        window.location.reload();
    }

    const displayProvableFair = () => {
        dispatch({
            type: Constants.ACTION_DISPLAY_PROVABLY_FAIR_PAGE,
            payload: {
                [Constants.PROVABLY_FAIR_INIT_DATA]: {
                    [Constants.OPEN]: true,
                    [Constants.ACTION_ORIGINATOR]: 0
                }
            }
        });
    }

    const initRecaptcha = () => {
        const script = document.createElement("script")
        script.src = `https://www.google.com/recaptcha/api.js?render=${Constants.SITE_KEY}`
        document.body.appendChild(script);
    }

    const ifCanRoll = () => {
        let loggedInUserString = UsersManager.getLoggedInUser();
        if (loggedInUserString) {
            let loggedInUser = JSON.parse(loggedInUserString);
            let lastRollTime = loggedInUser["lastFreeRollGameTime"];
            let currentTime = new Date().getTime();
            let distance = lastRollTime - currentTime;
            let minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
            return minutes <= 0;
        }
        return true;
    }

    useEffect(() => {
        initData()
        initRecaptcha();
    }, [docLoaded]);

    const initData = () => {
        axios.get(EndPoints.FREE_BTC_INIT)
            .then(data => {
                let results = data["data"]["data"]
                let newTableEntries: TableEntry[] = [];
                Object.keys(results).map((k) => {
                    let value = results[k];
                    let tableEntry = new TableEntry(k, value);
                    newTableEntries.push(tableEntry);
                });
                setTableEntries([...tableEntries, ...newTableEntries]);
                setLoading(false);
            }).catch(_error => {
                setErrorMessage("Failed to load rudimentary data. Please check your internet and refresh the page");
            });
    }

    useEffect(() => {
        let valueAtZero = tableEntries[0];
        tableEntries.splice(0, 1);
        setTableEntries([...tableEntries, valueAtZero]);
    }, [loading]);

    function getFreeRoll() {
        setRoll(-1);
        setRolled(false);
        setRolling(true);
        (window as any)
            .grecaptcha
            .ready(() => {
                // noinspection TypeScriptValidateJSTypes
                (window as any)
                    .grecaptcha
                    .execute(Constants.SITE_KEY, { action: Constants.FREE_ROLL_PAGE })
                    .then((token: any) => {
                        let loggedInUser = JSON.parse(UsersManager.getLoggedInUser() as string);
                        let postData = {
                            token: token,
                            gameType: GameType.FREE_ROLL,
                            clientSeed: localStorage.getItem(Constants.USER_CLIENT_SEED) ?? loggedInUser.activeClientSeed
                        };
                        axios.post(EndPoints.NEW_GAME, postData, {
                            headers: RequestUtils.getHeaders()
                        }).then(data => {
                            let responseData = data["data"]["data"];
                            let game = responseData["game"];
                            setGame(game);
                            let user = responseData["user"];
                            UsersManager.saveLoggedInUser(JSON.stringify(user));
                            dispatch({
                                type: Constants.LOGGED_IN_USER_DATA_CHANGED,
                                payload: JSON.stringify(user)
                            });
                            let wonAmount = game["wonAmount"];
                            let roll = game["luckyRoll"]
                            if (wonAmount > 0) {
                                setWonAmount(wonAmount)
                            }
                            setRolling(false);
                            setRolled(true);
                            setRoll(roll);
                            UsersManager.setHasRolled();
                            setTimeout(()=>{
                                setOpenDialog(true);
                            },2000);
                        }).catch(error => {
                            setRolling(false);
                            let errorMessage = ExceptionUtils.getErrorMessage(error, "Sorry, an error occurred. Please reload the page");
                            setErrorMessage(errorMessage);
                            setRolled(true);
                            setRoll(0)
                        });
                    });
            });
    }

    const moveToVerificationPage = (game: Game) => {
        GenUtils.moveToVerificationPage(game);
    }

    const VerifyView = React.forwardRef(function (props: any, ref: any) {
        return <Typography
            align={"center"}
            onClick={() => moveToVerificationPage(props.game)}
            style={{
                textDecoration: 'none',
                color: 'black',
                fontWeight: 600,
                paddingTop: 5,
                cursor: 'pointer',
                paddingBottom: 5
            }}>VERIFY</Typography>
    });

    return (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            marginLeft: inMobileView ? 20 : 200,
            marginRight: inMobileView ? 20 : 200,
            marginTop: 40,
        }}>
            <Card
                style={{
                    alignSelf: 'center',
                    flexDirection: 'row',
                    flexWrap: 'wrap',
                    display: 'flex',
                    padding: belowSevenHundred ? 5 : 2,
                    margin: 10,
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: 20,
                    borderRadius: blackCardsCanGoFlat ? 0 : belowSevenHundred ? 80 : 60,
                    backgroundColor: AppColors.BLACK
                }}>
                <FontAwesomeIcon icon={faLightbulb} style={{
                    color: AppColors.ACCENT_COLOR,
                    flexGrow: 1,
                    fontSize: 20,
                    marginTop: 2,
                    marginLeft: 10,
                }} />
                <Typography style={{
                    flexGrow: 1,
                    textAlign: 'center',
                    padding: 10,
                    fontSize: belowSevenHundred ? 16 : 18,
                    color: grey[300]
                }}
                    dangerouslySetInnerHTML={{ __html: `<span style='color:${grey[300]};font-size:${belowSevenHundred ? 16 : 18}px;'>Click the <b>ROLL</b> button to WIN UP TO <b>$200 IN FREE BITCOINS EVERY HOUR!</b></span>` }} />
            </Card>
            <img src={require('../images/free_bitcoin.png')} style={{
                alignSelf: 'center',
            }} alt={"Free Bitcoin Icon"} />
            <Card style={{
                backgroundColor: AppColors.ACCENT_COLOR,
                padding: 10,
                marginTop: 10,
            }}>
                <Typography style={{ textAlign: 'center' }}
                    dangerouslySetInnerHTML={{ __html: `<span>PLAY <b><a href='${Routes.MULTIPLY_BTC}' style='text-decoration:none;color:black;'>MULTIPLY BTC</a></b> to increase your <b>FREE BTC REWARDS</b></span>` }} />
            </Card>
            <div style={{
                display: 'flex',
                paddingTop: 10,
                flexDirection: 'row',
                alignItems: 'center'
            }}>
                <Typography
                    style={{
                        fontSize: 13,
                        color: grey[300],
                        textAlign: 'center',
                        alignSelf: 'center',
                        lineHeight: '1.6',
                    }}>This Game is </Typography>
                <Typography
                    onClick={displayProvableFair}
                    style={{
                        color: AppColors.CYAN,
                        paddingLeft: 5,
                        fontSize: 13,
                        cursor: 'pointer',
                        textTransform: 'none'
                    }}>Provably Fair</Typography>
            </div>
            <TableContainer>
                <Card
                    style={{ backgroundColor: AppColors.CARD_BACKGROUND_COLOR, marginTop: 10 }}>
                    <Table
                        style={{
                            marginTop: 15,
                            borderCollapse: 'collapse'
                        }}>
                        <TableHead>
                            <TableRow>
                                <StyledTableCell align="center">LUCKY NUMBER</StyledTableCell>
                                <StyledTableCell align="center">PRICE(BTC)</StyledTableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {!loading &&
                                tableEntries.map((entry: TableEntry) => {
                                    let k = entry.key;
                                    let keyPair = k.split("to");
                                    let value = entry.value;
                                    let start = keyPair[0];
                                    let end = keyPair[1] ?? start;
                                    let price = NumberUtils.toFixed(value);
                                    return <StyledTableRow key={k}>
                                        {
                                            end !== '10000' &&
                                            <StyledTableCell
                                                align="center">{start} - {end}</StyledTableCell>
                                        }
                                        {
                                            end === '10000' &&
                                            <StyledTableCell
                                                align="center">{start}</StyledTableCell>
                                        }
                                        <StyledTableCell
                                            align="center">{`${price}`}</StyledTableCell>
                                    </StyledTableRow>
                                })
                            }
                        </TableBody>
                    </Table>
                    {
                        loading && !errorMessage &&
                        <div style={{
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'center',
                            alignItems: 'center',
                            padding: 20
                        }}>
                            <CircularProgress
                                color="inherit"
                                size={40}
                                style={{
                                    marginTop: 30,
                                    color: 'white',
                                }} />
                        </div>
                    }
                </Card>
            </TableContainer>
            <div style={{ marginTop: 20 }} />
            <div
                style={{ marginBottom: 20 }}
                className="g-recaptcha"
                data-sitekey={Constants.SITE_KEY}
                data-size="invisible"
            />
            <DigitsView roll={roll} rolled={rolled} />
            {
                ifCanRoll() &&
                <Button onClick={() => getFreeRoll()}
                    style={{
                        backgroundColor: AppColors.ACCENT_COLOR,
                        fontWeight: 600,
                        fontSize: 20,
                        marginTop: 20,
                        width: 100
                    }}
                    disabled={rolling}>ROLL</Button>
            }
            {
                errorMessage &&
                <Card style={{
                    background: red[300],
                    borderRadius: 2,
                    padding: 10,
                    marginTop: 10
                }}>
                    <Typography style={{
                        fontWeight: 500
                    }} align={"center"} dangerouslySetInnerHTML={{ __html: `${errorMessage}` }} />
                </Card>
            }
            {
                rolled && roll > 0 &&
                <Card
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        background: green[200],
                        borderRadius: 2,
                        paddingTop: 10,
                        paddingBottom: 10,
                        paddingLeft: 30,
                        paddingRight: 30,
                        marginTop: 10
                    }}>
                    <Typography style={{
                        fontWeight: 500
                    }} align={"center"}
                        dangerouslySetInnerHTML={{ __html: `<b>You</b> rolled <b>${roll}</b> and so you win <b>${wonAmount.toFixed(8)}</b>BTC` }} />
                    <VerifyView game={game} />
                </Card>
            }
            {
                !ifCanRoll() &&
                <NextRollCountDownTimer headerMessage={errorMessage} callBack={freeRollCountDownTimerCallBack} />
            }
            <LiveStats gameType={0} />
            <Dialog disableBackdropClick={true}
                open={openDialog}
                onClose={() => handleDialogClose(false)}
                aria-labelledby="responsive-dialog-title"
                PaperProps={{
                    style: {
                        background: yellow[100]
                    }
                }}
            >
                <DialogTitle style={{
                    textAlign: 'center',
                    fontWeight: 600,
                    fontStyle: 'bold',
                }} id="responsive-dialog-title">{"MULTIPLY YOUR WINNINGS"}</DialogTitle>
                <DialogContent>
                    <Typography align={'center'}
                        style={{
                            fontSize: 18,
                        }}
                        dangerouslySetInnerHTML={{
                            __html: `Why not <b>MULTIPLY</b> your WINNINGS up to <b><span style='color:darkorange;font-size:25px'>4,750</span></b> times by playing our <b>Simple 6/49 Game!</b>
`}}>
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => handleDialogClose(false)} color="primary">
                        CLOSE
                    </Button>
                    <Button autoFocus onClick={() => handleDialogClose(true)} color="primary">
                        MULTIPLY
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
});

export default FreeBTC;